import { useCallback, useMemo, useState } from "react";
import { FileUp, History } from "lucide-react";
import type { MugsManualChange, MugsManualChangeDecision, MugsManualChangeSummary, MugsManualRelease } from "../types";
import { getApi } from "../api";
import { useAsyncResource } from "../hooks/useAsyncResource";
import { usePageSetup } from "../hooks/usePageSetup";
import { useShortcuts } from "../hooks/useShortcuts";
import { notify } from "../store/uiStore";
import { reportError } from "../services/eventBus";
import { trackEvent } from "../telemetry/appInsights";
import { PageHeader } from "../components/PageHeader";
import { Card } from "../components/Card";
import { Button } from "../components/Button";
import { FieldRow } from "../components/FieldRow";
import { DataGrid, type GridColumn } from "../components/DataGrid";
import { EmptyState, ErrorState } from "../components/ScreenStates";
import { DateInput, FileUpload, TextArea, TextInput } from "../components/fields";
import { MugsManualChangePreview } from "../components/actuarial/MugsManualChangePreview";
import { formatDate } from "../utils/format";

const DEFAULT_SORT = { key: "loadedUtc", direction: "desc" } as const;

const SOURCE_LABELS: Record<MugsManualRelease["source"], string> = {
  Milliman: "Milliman",
  WebMugsArchive: "WebMUGs archive",
  CurrentCatalogSeed: "Catalog seed",
  CustomSeed: "Custom seed",
};

interface StagedPreview {
  summary: MugsManualChangeSummary;
  changes: MugsManualChange[];
}

/**
 * Upload a MUGS manual release (two CSV files), read the preview of everything it would change, ignore the rare row that
 * looks wrong, apply. Applied releases are immutable and appear in the history with their change counts.
 */
export function MugsManualUploadPage() {
  usePageSetup("MUGS Manual Upload", null);
  const history = useAsyncResource(() => getApi().listMugsManuals(), "mugs-manual-history", "MugsManualUploadPage.history");
  const [name, setName] = useState("");
  const [effectiveFrom, setEffectiveFrom] = useState("");
  const [notes, setNotes] = useState("");
  const [conditionFile, setConditionFile] = useState<File | null>(null);
  const [ratingFile, setRatingFile] = useState<File | null>(null);
  const [nameError, setNameError] = useState<string | null>(null);
  const [staged, setStaged] = useState<StagedPreview | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const [decidingChangeId, setDecidingChangeId] = useState<string | null>(null);

  const resetForm = useCallback(() => {
    setStaged(null);
    setConditionFile(null);
    setRatingFile(null);
    setName("");
    setEffectiveFrom("");
    setNotes("");
  }, []);

  const loadPreview = useCallback(async (mugsManualId: string) => {
    setIsLoadingPreview(true);
    try {
      const [summary, changes] = await Promise.all([getApi().getMugsManualSummary(mugsManualId), getApi().listMugsManualChanges(mugsManualId, null)]);
      setStaged({ summary, changes });
    } catch (error) {
      reportError(error, "MugsManualUploadPage.preview");
    } finally {
      setIsLoadingPreview(false);
    }
  }, []);

  const handleUpload = useCallback(async () => {
    if (!conditionFile || !ratingFile) return;
    if (name.trim() === "") {
      setNameError("Release name is required");
      return;
    }
    setNameError(null);
    setIsUploading(true);
    try {
      const summary = await getApi().stageMugsManual({ name: name.trim(), effectiveFrom: effectiveFrom || null, notes: notes.trim() || null, conditionFile, ratingFile });
      await loadPreview(summary.mugsManualId);
      history.reload();
    } catch (error) {
      reportError(error, "MugsManualUploadPage.upload");
    } finally {
      setIsUploading(false);
    }
  }, [conditionFile, ratingFile, name, effectiveFrom, notes, loadPreview, history]);

  const handleDecide = useCallback(
    async (change: MugsManualChange, decision: MugsManualChangeDecision) => {
      if (!staged) return;
      setDecidingChangeId(change.id);
      try {
        await getApi().decideMugsManualChange(staged.summary.mugsManualId, change.id, decision);
        const summary = await getApi().getMugsManualSummary(staged.summary.mugsManualId);
        setStaged((current) => (current ? { summary, changes: current.changes.map((entry) => (entry.id === change.id ? { ...entry, decision } : entry)) } : current));
      } catch (error) {
        reportError(error, "MugsManualUploadPage.decide");
      } finally {
        setDecidingChangeId(null);
      }
    },
    [staged],
  );

  const handleApply = async () => {
    if (!staged) return;
    setIsApplying(true);
    try {
      const result = await getApi().applyMugsManual(staged.summary.mugsManualId);
      trackEvent("MugsManualApplied", { name: staged.summary.name, ratingsCreated: String(result.ratingsCreated), ratingsUpdated: String(result.ratingsUpdated), changesIgnored: String(result.changesIgnored) });
      notify(`Applied MUGS manual ${staged.summary.name}: ${result.ratingsCreated} new ratings, ${result.ratingsUpdated} updated, ${result.ratingsRetired} retired, ${result.changesIgnored} ignored. The shared catalog was refreshed for Skynet.`, "success");
      resetForm();
      history.reload();
    } catch (error) {
      reportError(error, "MugsManualUploadPage.apply");
    } finally {
      setIsApplying(false);
    }
  };

  const handleDiscard = useCallback(async () => {
    if (!staged) return;
    try {
      await getApi().discardMugsManual(staged.summary.mugsManualId);
      notify(`Discarded ${staged.summary.name}. Nothing in the catalog changed.`, "info");
      resetForm();
      history.reload();
    } catch (error) {
      reportError(error, "MugsManualUploadPage.discard");
    }
  }, [staged, resetForm, history]);

  const shortcuts = useMemo(() => ({ save: () => void handleUpload(), cancel: () => void handleDiscard() }), [handleUpload, handleDiscard]);
  useShortcuts(shortcuts);

  const columns = useMemo<GridColumn<MugsManualRelease>[]>(
    () => [
      {
        key: "name",
        header: "Release",
        sortValue: (row) => row.name,
        render: (row) => (
          <span className="font-medium text-gray-800">
            {row.name}
            {row.isCurrent && <span className="ml-2 rounded-full bg-brand-green-50 px-2 py-0.5 text-xs font-medium text-brand-green-dark">Current</span>}
          </span>
        ),
      },
      { key: "effectiveFrom", header: "Effective", sortValue: (row) => row.effectiveFrom ?? "", render: (row) => (row.effectiveFrom ? formatDate(row.effectiveFrom) : <span className="text-gray-400">–</span>) },
      { key: "source", header: "Source", sortValue: (row) => row.source, render: (row) => SOURCE_LABELS[row.source] },
      { key: "status", header: "Status", sortValue: (row) => row.status, render: (row) => row.status },
      { key: "loadedUtc", header: "Loaded", sortValue: (row) => row.loadedUtc, render: (row) => formatDate(row.loadedUtc) },
      { key: "appliedUtc", header: "Applied", sortValue: (row) => row.appliedUtc ?? "", render: (row) => (row.appliedUtc ? formatDate(row.appliedUtc) : <span className="text-gray-400">–</span>) },
      { key: "conditions", header: "Conditions", align: "right", sortValue: (row) => row.conditions, render: (row) => row.conditions.toLocaleString() },
      { key: "ratings", header: "Ratings", align: "right", sortValue: (row) => row.ratings, render: (row) => row.ratings.toLocaleString() },
      { key: "changes", header: "Changes", align: "right", sortValue: (row) => row.changes, render: (row) => row.changes.toLocaleString() },
      { key: "ignored", header: "Ignored", align: "right", sortValue: (row) => row.ignored, render: (row) => row.ignored.toLocaleString() },
      {
        key: "actions",
        header: "Actions",
        hideHeader: true,
        align: "right",
        render: (row) =>
          row.status === "Staged" ? (
            <Button variant="ghost" size="sm" onClick={() => void loadPreview(row.id)} aria-label={`Resume ${row.name}`}>
              Resume
            </Button>
          ) : null,
      },
    ],
    [loadPreview],
  );

  return (
    <>
      <PageHeader title="MUGS Manual Upload" subtitle="Upload a Milliman release as its two CSV files, review everything it changes, then apply. Debit overrides are never touched by a release." />
      <div className="space-y-5">
        <Card title="MUGS Manual Release" icon={FileUp}>
          <div className="space-y-4">
            <FieldRow>
              <TextInput id="release-name" label="Release name" value={name} onChange={setName} error={nameError} required placeholder="e.g. 2027" />
              <DateInput id="release-effective" label="Effective from" value={effectiveFrom} onChange={setEffectiveFrom} hint="Optional" />
            </FieldRow>
            <FieldRow>
              <FileUpload id="condition-file" label="Conditions file (MugsCondition.csv)" acceptExtensions={["csv"]} file={conditionFile} onFile={(next) => { setConditionFile(next); setStaged(null); }} required />
              <FileUpload id="rating-file" label="Ratings file (MugsConditionRating.csv)" acceptExtensions={["csv"]} file={ratingFile} onFile={(next) => { setRatingFile(next); setStaged(null); }} required />
            </FieldRow>
            <TextArea id="release-notes" label="Notes" value={notes} onChange={setNotes} rows={1} />
            <div className="flex justify-end">
              <Button onClick={() => void handleUpload()} disabled={!conditionFile || !ratingFile} isLoading={isUploading}>
                Upload and preview changes
              </Button>
            </div>
          </div>
        </Card>
        {staged && (
          <Card title={`Preview for ${staged.summary.name}`}>
            <MugsManualChangePreview summary={staged.summary} changes={staged.changes} isLoading={isLoadingPreview} decidingChangeId={decidingChangeId} onDecide={(change, decision) => void handleDecide(change, decision)} />
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="secondary" onClick={() => void handleDiscard()} disabled={isApplying}>
                Discard
              </Button>
              <Button onClick={() => void handleApply()} isLoading={isApplying}>
                Apply
              </Button>
            </div>
          </Card>
        )}
        <Card title="Release History" icon={History}>
          {history.state.kind === "error" && <ErrorState message={history.state.message} onRetry={history.reload} />}
          {history.state.kind !== "error" && <DataGrid caption="MUGS manual release history" columns={columns} rows={history.data ?? []} rowKey={(row) => row.id} isLoading={history.state.kind === "loading"} defaultSort={DEFAULT_SORT} emptyState={<EmptyState message="No releases yet." />} />}
        </Card>
      </div>
    </>
  );
}
